// ThreadLayer — on a phone a thread is a full-screen layer over the room.
// The room stays mounted underneath (its scroll never moves) and is
// revealed again by closing or swiping right.
//
// Motion uses the browser's own view transitions: everything else fades
// out, the tapped message slides to the top at its exact size (same width,
// so nothing rewraps), and only then the thread fades in around it.
// Closing is the same in reverse.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type PointerEvent,
} from "react";
import { flushSync } from "react-dom";

import { MobileThread, THREAD_ROOT_TRANSITION } from "~/components/mobile/MobileThread";
import { mobileStrings } from "~/components/mobile/strings";
import type { TimelineEvent } from "~/lib/matrix";

/** Reply preview under the root in the room; it has no twin in the thread, so it fades with the room. */
const THREAD_TAIL_TRANSITION = "thread-tail";

const SETTLE_MS = 200;
/** Matches `::view-transition-new(root)` in main.css: the room fades in once the message lands. */
const ROOM_FADE_IN_DELAY_MS = 420;
const TAIL_FADE_MS = 180;
const EASE = "cubic-bezier(0.2, 0, 0, 1)";
/** How long a close waits for the layer to leave the DOM. */
const CLOSE_WAIT_MS = 400;
/** Horizontal travel before a touch counts as a swipe, not a tap or scroll. */
const SWIPE_SLOP_PX = 8;
/** Share of the card width, or a flick speed, that closes on release. */
const SWIPE_CLOSE_SHARE = 0.3;
const SWIPE_CLOSE_VELOCITY = 0.5;
/** A swipe starts within this distance of the card's left edge. */
const SWIPE_EDGE_PX = 44;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function canTransition(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof document.startViewTransition === "function" &&
    !prefersReducedMotion()
  );
}

function roomRow(rootId: string): HTMLElement | null {
  const rows = document.querySelectorAll<HTMLElement>(`[data-event-id="${CSS.escape(rootId)}"]`);
  for (const row of rows) {
    if (row.closest("[data-testid='thread-layer']")) continue;
    const rect = row.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) return row;
  }
  return null;
}

function rowTail(row: HTMLElement): HTMLElement | null {
  return row.querySelector<HTMLElement>("[data-testid='thread-tail']");
}

function nameRow(row: HTMLElement, named: boolean): void {
  row.style.viewTransitionName = named ? THREAD_ROOT_TRANSITION : "";
  const tail = rowTail(row);
  if (tail) tail.style.viewTransitionName = named ? THREAD_TAIL_TRANSITION : "";
}

/**
 * Open a thread so its root visibly travels from the room to the top.
 * `update` must synchronously put the thread layer on screen.
 */
export function openThreadWithTransition(rootId: string, update: () => void): void {
  const row = canTransition() ? roomRow(rootId) : null;
  if (!row) {
    update();
    return;
  }
  nameRow(row, true);
  document.startViewTransition(() => {
    nameRow(row, false);
    flushSync(update);
  });
}

// Rendering (and so requestAnimationFrame) is suspended while a view
// transition's update callback runs, so this must not poll on frames.
function waitUntilGone(el: Element): Promise<void> {
  return new Promise((resolve) => {
    if (!el.isConnected) return resolve();
    const observer = new MutationObserver(() => {
      if (el.isConnected) return;
      observer.disconnect();
      resolve();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve();
    }, CLOSE_WAIT_MS);
  });
}

export interface ThreadLayerProps {
  roomId: string;
  root: TimelineEvent;
  roomName: string;
  onClose: () => void;
}

export function ThreadLayer({ roomId, root, roomName, onClose }: ThreadLayerProps) {
  const titleId = useId();
  const layerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const closingRef = useRef(false);
  const dragRef = useRef<{ x: number; y: number; t: number; active: boolean } | null>(null);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    const layer = layerRef.current;
    const cardRoot = layer?.querySelector<HTMLElement>("[data-thread-root]") ?? null;
    if (!layer || !cardRoot || !canTransition()) {
      onClose();
      return;
    }
    // The root only travels back if it is on screen; otherwise the thread
    // simply fades to the room, since a message arriving from off-screen
    // reads as a glitch.
    const scroller = cardRoot.parentElement;
    const travels =
      scroller !== null &&
      cardRoot.getBoundingClientRect().bottom >
        scroller.getBoundingClientRect().top + SWIPE_EDGE_PX;
    const row = travels ? roomRow(root.id) : null;
    const html = document.documentElement;
    if (!row) html.dataset.threadTransition = "fade";
    // On the way back the reply preview stays hidden until the message has
    // landed, then fades in: a delayed fade on its own transition layer is
    // ignored by WebKit, which showed it on top from the first frame.
    const tail = row ? rowTail(row) : null;
    const transition = document.startViewTransition(async () => {
      cardRoot.style.viewTransitionName = "";
      if (row) row.style.viewTransitionName = THREAD_ROOT_TRANSITION;
      if (tail) tail.style.visibility = "hidden";
      onClose();
      await waitUntilGone(layer);
    });
    // The new-state picture is live, so revealing the preview once the
    // message has landed fades it in together with the room.
    const revealTail = () => {
      if (!tail || tail.style.visibility !== "hidden") return;
      tail.style.visibility = "";
      if (typeof tail.animate === "function") {
        tail.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: TAIL_FADE_MS,
          easing: "linear",
        });
      }
    };
    void transition.ready.then(() => setTimeout(revealTail, ROOM_FADE_IN_DELAY_MS), revealTail);
    void transition.finished.finally(revealTail);
    void transition.finished.finally(() => {
      if (row) nameRow(row, false);
      delete html.dataset.threadTransition;
    });
  }, [onClose, root.id]);

  useEffect(() => {
    cardRef.current?.querySelector<HTMLElement>(`[id="${CSS.escape(titleId)}"]`)?.focus();
  }, [titleId]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      requestClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  function onPointerDown(event: PointerEvent<HTMLElement>) {
    const card = cardRef.current;
    if (!card || closingRef.current) return;
    if (event.clientX - card.getBoundingClientRect().left > SWIPE_EDGE_PX) return;
    dragRef.current = { x: event.clientX, y: event.clientY, t: event.timeStamp, active: false };
  }

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    const drag = dragRef.current;
    const card = cardRef.current;
    const scrim = scrimRef.current;
    if (!drag || !card || !scrim) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.active) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > SWIPE_SLOP_PX) {
        dragRef.current = null;
        return;
      }
      if (dx < SWIPE_SLOP_PX) return;
      drag.active = true;
      card.setPointerCapture(event.pointerId);
    }
    const offset = Math.max(0, dx);
    card.style.transform = `translateX(${offset}px)`;
    scrim.style.opacity = String(Math.max(0, 1 - offset / card.offsetWidth));
  }

  function onPointerUp(event: PointerEvent<HTMLElement>) {
    const drag = dragRef.current;
    dragRef.current = null;
    const card = cardRef.current;
    const scrim = scrimRef.current;
    if (!drag?.active || !card || !scrim || typeof card.animate !== "function") return;
    const dx = Math.max(0, event.clientX - drag.x);
    const velocity = dx / Math.max(1, event.timeStamp - drag.t);
    const width = card.offsetWidth;
    const from = card.style.transform;
    const fromOpacity = scrim.style.opacity;
    card.style.transform = "";
    scrim.style.opacity = "";
    const options: KeyframeAnimationOptions = {
      duration: SETTLE_MS,
      easing: EASE,
      fill: "forwards",
    };
    if (dx > width * SWIPE_CLOSE_SHARE || velocity > SWIPE_CLOSE_VELOCITY) {
      closingRef.current = true;
      scrim.animate([{ opacity: fromOpacity }, { opacity: 0 }], options);
      void card
        .animate([{ transform: from }, { transform: `translateX(${width}px)` }], options)
        .finished.then(onClose, onClose);
      return;
    }
    card.animate([{ transform: from }, { transform: "none" }], {
      duration: SETTLE_MS,
      easing: EASE,
    });
    scrim.animate([{ opacity: fromOpacity }, { opacity: 1 }], {
      duration: SETTLE_MS,
      easing: EASE,
    });
  }

  return (
    <div ref={layerRef} className="fixed inset-0 z-40" data-testid="thread-layer">
      <div
        ref={scrimRef}
        aria-hidden
        onClick={requestClose}
        className="absolute inset-0"
        style={{ background: "hsl(var(--background) / 0.6)" }}
      />
      <section
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={mobileStrings.thread.dialogLabel(roomName)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute inset-0 flex flex-col overflow-hidden bg-background chat-safe-top"
        style={
          {
            touchAction: "pan-y",
            "--chat-phone-bottom-inset": "max(0px, calc(env(safe-area-inset-bottom, 0px) - 16px))",
          } as CSSProperties
        }
      >
        <MobileThread
          roomId={roomId}
          root={root}
          roomName={roomName}
          titleId={titleId}
          onClose={requestClose}
        />
      </section>
    </div>
  );
}
